import React, { useCallback, useRef, useState } from 'react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { BtnBase, ConfirmationDialog, IconBtnBase, ResourcesList } from 'Components';
import {
    clearCertificatePrints,
    clearCertificateVerifications,
} from 'Services/CertificateNamesSvc';
import VerificationsTable from './VerificationsTable';
import PrintsTable from './PrintsTable';

const VERIFICATIONS_TAB = 'verifications';
const PRINTS_TAB = 'prints';

/**
 * Two related logs behind one header, the way the Participations page puts its
 * filters in Tabs: what was checked, and what was actually printed.
 */
const VerificationHistoryPage = () => {
    const { t } = useTranslation();
    const [tab, setTab] = useState(VERIFICATIONS_TAB);
    const [confirmingClear, setConfirmingClear] = useState(false);
    const [clearing, setClearing] = useState(false);

    // Each tab owns its own query, so the header Refresh reaches whichever table
    // is mounted through a callback that table registers on mount.
    const refetchRef = useRef(null);
    const registerRefetch = useCallback((refetch) => {
        refetchRef.current = refetch;
    }, []);

    const clearingVerifications = tab === VERIFICATIONS_TAB;

    /**
     * Clear whichever log is on screen.
     *
     * The two are not symmetric, and the dialog says so before anything is sent:
     * clearing verifications takes the prints with it, because a print row is a
     * child of the attempt that produced it and the database cascades. Clearing
     * prints touches only prints. The toast reports the counts the server
     * actually deleted rather than assuming the call emptied anything.
     */
    const handleClear = async () => {
        setConfirmingClear(false);
        setClearing(true);

        try {
            if (clearingVerifications) {
                const { verifications = 0, prints = 0 } = await clearCertificateVerifications();

                toast.success(
                    verifications || prints
                        ? t('admin.certificateNames.history.clearedVerifications', {
                              verifications,
                              prints,
                          })
                        : t('admin.certificateNames.history.nothingToClear'),
                );
            } else {
                const { prints = 0 } = await clearCertificatePrints();

                toast.success(
                    prints
                        ? t('admin.certificateNames.history.clearedPrints', { prints })
                        : t('admin.certificateNames.history.nothingToClear'),
                );
            }

            refetchRef.current?.();
        } catch (error) {
            toast.error(error?.response?.data?.message);
        } finally {
            setClearing(false);
        }
    };

    return (
        <>
            <ResourcesList
                headerProps={{
                    title: t('admin.certificateNames.history.title'),
                    EndSide: (
                        <div className="flex items-center gap-2">
                            {/* Spelled out rather than left as an icon: this empties a log for
                                good, and an unlabelled bin next to Refresh is a thing people
                                click to find out what it does. Red and named, it is not. */}
                            <BtnBase
                                onClick={() => setConfirmingClear(true)}
                                isLoading={clearing}
                                color="error"
                                variant="outlined"
                                fullWidth={false}
                                startIcon={<DeleteSweepOutlinedIcon />}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                {clearingVerifications
                                    ? t('admin.certificateNames.history.clearVerifications')
                                    : t('admin.certificateNames.history.clearPrints')}
                            </BtnBase>

                            <IconBtnBase
                                onClick={() => {
                                    refetchRef.current?.();
                                }}
                                size="large"
                            >
                                <RefreshIcon />
                            </IconBtnBase>
                        </div>
                    ),
                }}
                Section={
                    <div className="mt-8">
                        <Tabs
                            value={tab}
                            onChange={(event, newValue) => setTab(newValue)}
                            aria-label={t('admin.certificateNames.history.title')}
                            centered
                        >
                            <Tab
                                label={t('admin.certificateNames.history.verifications')}
                                value={VERIFICATIONS_TAB}
                            />
                            <Tab
                                label={t('admin.certificateNames.history.prints')}
                                value={PRINTS_TAB}
                            />
                        </Tabs>

                        {tab === VERIFICATIONS_TAB ? (
                            <VerificationsTable registerRefetch={registerRefetch} />
                        ) : (
                            <PrintsTable registerRefetch={registerRefetch} />
                        )}
                    </div>
                }
            />

            <ConfirmationDialog
                isOpen={confirmingClear}
                title={t(
                    clearingVerifications
                        ? 'admin.certificateNames.history.clearVerificationsTitle'
                        : 'admin.certificateNames.history.clearPrintsTitle',
                )}
                text={t(
                    clearingVerifications
                        ? 'admin.certificateNames.history.clearVerificationsText'
                        : 'admin.certificateNames.history.clearPrintsText',
                )}
                onReject={() => setConfirmingClear(false)}
                onResolve={handleClear}
            />
        </>
    );
};

export default VerificationHistoryPage;
